import { Dialog } from "construct-ui";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import SelectUserComponent from "../../components/user/select-user.component";
import { codeMasterType } from "../../config/constants";
import codeMasterModel from "../../models/common/code-master.model";
import DivisionModel from "../../models/division/division.model";
import SiteManagementModel from "../../models/site-management/site-management.model";
import ViewBase from "../view-base.view";

/**
 * Site Management - Add / Update Contact Information Dialog View
 * 
 * A dialog page that allows users to add/update contact information
 * 
 * @author CV Leong
 */
export default function SiteManagementContactUpdateDialogView() {
  ViewBase.call(this);

  let _onClose = () => { };
  let contactForm = {};
  let currentSiteId = '';
  let divisionOptions = [];
  let userSiteRelationOptions = [];

  const DEFAULT_CONTACT_FORM = {
    contactId: 0,
    productHierarchy: '',
    contactUserId: '',
    contactName: '',
    // contactNumber: '', // To be removed as suggested by Tommy on 2023-10-11
    relationCode: '',
    remark: '',
  }

  const submit = (e) => {
    // Form validation
    if (!this.validateForm(e)) {
      return false;
    }

    addOrUpdateContact();

    return false;
  }

  const addOrUpdateContact = () => {
    this.showLoading();
    SiteManagementModel.addOrUpdateSiteContactInfo(currentSiteId, contactForm)
      .then(() => {
        _onClose(true); // true: re-load contact list
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  const loadForm = (siteId, contactData = DEFAULT_CONTACT_FORM) => {
    contactForm = { ...contactData };
    currentSiteId = siteId;
    m.redraw();
  }

  const getDivisionOptions = () => {
    divisionOptions.splice(0);

    DivisionModel.getDivisionList()
      .then((result) => {
        result?.map(i => {
          divisionOptions.push({ ...i })
        })
      })
      .catch((error) => {
        divisionOptions.splice(0);
        this.handleError(error);
      })
  }

  const getUserSiteRelationOptions = () => {
    userSiteRelationOptions.splice(0);

    codeMasterModel.getByCategoryV2({ category: codeMasterType.userSiteRelation })
      .then((result) => {
        result?.map(i => {
          userSiteRelationOptions.push({ code: i.code, name: i.name })
        })
      })
      .catch((error) => {
        userSiteRelationOptions.splice(0);
        this.handleError(error);
      })
  }

  return {
    oninit: () => {
      getDivisionOptions();
      getUserSiteRelationOptions();
    },
    view: ({ attrs: {
      isOpen,
      onClose,
      data,
      siteId
    } }) => {
      return [
        m(Dialog, {
          addToStack: true,
          class: 'cui-dialog-xl',
          title: data
            ? this.getText('LT00000880', 'Update Contact Information')
            : this.getText('LT00000879', 'Add Contact Information'),
          isOpen: isOpen,
          onClose: onClose,
          onClosed: () => { },
          onOpened: () => { loadForm(siteId, data); _onClose = onClose; },
          closeOnOutsideClick: false,
          content: [
            m('.card',
              m('.card-body',
                m('form#addOrUpdateContactForm.row.g-4.g-md-2.needs-validation[novalidate]', {
                  onsubmit: submit
                },
                  m('.col-md-4.position-relative',
                    m('label.form-label.mandatory[for=contactPerson]', this.getText('LT00000838', 'Contact Person')),
                    m(SelectUserComponent, {
                      id: 'contactPerson',
                      multiple: false,
                      required: true,
                      selectedUserList: contactForm.contactUserId
                        ? [{ userID: contactForm.contactUserId, userName: contactForm.contactName }]
                        : null,
                      onSelect: (value) => {
                        contactForm.contactUserId = value[0]?.userID ?? '';
                        contactForm.contactName = value[0]?.userName ?? '';
                      }
                    }),
                    m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                  ),
                  m('.col-md-4.position-relative',
                    m('label.form-label.mandatory[for=relation]', this.getText('LT00000875', 'Relation')),
                    m(SingleSelectComponent, {
                      id: 'relation',
                      items: userSiteRelationOptions,
                      keyOnly: true,
                      required: true,
                      selectedItem: contactForm.relationCode,
                      onSelect: (value) => {
                        contactForm.relationCode = value;
                      }
                    }),
                    m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                  ),
                  /** To be removed as suggested by Tommy on 2023-10-11 */
                  // m('.col-sm-6.position-relative',
                  //   m('label.form-label[for=contactNumber]', this.getText('LT00000247', 'Contact Number')),
                  //   m('input.form-control', {
                  //     id: 'contactNumber',
                  //     type: 'tel',
                  //     value: contactForm.contactNumber,
                  //     placeholder: this.getText('LT00000247', 'Contact Number'),
                  //     onchange: (e) => {
                  //       contactForm.contactNumber = e.target.value;
                  //     }
                  //   })
                  // ),
                  m('.col-md-4.position-relative',
                    m('label.form-label.mandatory[for=productHierarchy]', this.getText('LT00000874', 'Product Hierarchy')),
                    m(SingleSelectComponent, {
                      id: 'productHierarchy',
                      items: divisionOptions,
                      keyOnly: true,
                      required: true,
                      selectedItem: contactForm.productHierarchy,
                      onSelect: (value) => {
                        contactForm.productHierarchy = value;
                      }
                    }),
                    m('.invalid-tooltip', this.getText('LT00000729', 'This field is required')),
                  ),
                  m('.col-12.position-relative',
                    m('label.form-label[for=remarks]', this.getText('LT00000152', 'Remarks')),
                    m('textarea.form-control', {
                      id: 'remarks',
                      value: contactForm.remark,
                      placeholder: this.getText('LT00000152', 'Remarks'),
                      onchange: (e) => {
                        contactForm.remark = e.target.value;
                      }
                    }),
                  ),
                ) // form - end
              )
            ), // card - end
            this.getBaseViews()
          ],
          footer: [
            m('.d-flex.justify-content-end.gap-2',
              m('button.btn.btn-primary[form="addOrUpdateContactForm"][type="submit"]', {
              }, this.getText('LT00000005', 'Submit')),
              m('button.btn.btn-white', {
                onclick: () => { onClose(false) },
              }, this.getText('LT00000021', 'Close')),
            )
          ],
        })
      ];
    }
  };
}
